<template>
  <div>
    <BaseButton
      v-if="menuCollapsed"
      :layout="'icon-only'"
      :icon="'BaseMore'"
      :dark-bg="darkBg"
      @click="expandMenu"
      :aria-expanded="(!menuCollapsed).toString()"
      aria-controls="mobile-menu-panel"
      :aria-label="$t('Shared.menu')"
      data-cy="headerPanelExpandMenuButton"
    />

    <div
      id="mobile-menu-panel"
      ref="menuPanel"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('Shared.menu')"
      :aria-hidden="menuCollapsed.toString()"
      tabindex="-1"
      class="fixed top-0 bottom-0 z-50 bg-gray-900 transition-all text-white overflow-y-auto"
      :style="'right: -' + (menuCollapsed ? '100vw' : '0') + '; width: 100vw;'"
      @keydown.esc="closeMenu"
    >
      <div class="sticky top-0 z-10 bg-gray-900 px-4 pt-4 pb-2 flex justify-between items-center border-b border-gray-700">
        <div class="text-xl p-2">{{ $t('Shared.menu') }}</div>
        <BaseButton
          v-if="!menuCollapsed"
          :layout="'icon-only'"
          :icon="'BaseClose'"
          :dark-bg="true"
          :aria-label="$t('Shared.close')"
          @click="closeMenu"
        />
      </div>

      <div class="px-4 pb-8">
        <nav class="mt-3 flex flex-col" :aria-label="$t('Shared.menu')">
          <router-link
            :to="'/'"
            class="mobile-nav-link py-3 px-3 rounded-md text-white hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10"
            @click="closeMenu"
          >
            {{ $t('Unauthorized.go_home') }}
          </router-link>

          <router-link
            v-if="user"
            :to="'/mine-projekter'"
            class="mobile-nav-link py-3 px-3 rounded-md text-white hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10"
            @click="closeMenu"
          >
            {{ $t('Header.Button03') }}
          </router-link>

          <router-link
            v-if="user && !hasAccessToBokvalitet"
            :to="'/ams'"
            class="mobile-nav-link py-3 px-3 rounded-md text-white hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10"
            @click="closeMenu"
          >
            {{ $t('Header.Button06') }}
          </router-link>

          <router-link
            v-if="(isAdmin || isSuperAdmin) && !hasAccessToBokvalitet"
            :to="'/gms'"
            class="mobile-nav-link py-3 px-3 rounded-md text-white hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10"
            @click="closeMenu"
          >
            {{ $t('Header.Button14') }}
          </router-link>

          <button
            v-if="isSuperAdmin || isAdmin"
            class="py-3 px-3 rounded-md hover:bg-gray-500 hover:bg-opacity-10 w-full text-left flex items-center justify-between"
            type="button"
            :aria-expanded="showAdminMenu.toString()"
            aria-controls="mobile-menu-admin-section"
            @click="showAdminMenu = !showAdminMenu"
          >
            <span>{{ $t('Header.AdminMenuLabel') }}</span>
            <BaseIcon
              icon="BaseChevronDown"
              size="sm"
              color="white"
              :class="showAdminMenu ? 'rotate-180' : ''"
            />
          </button>

          <div
            v-if="showAdminMenu"
            id="mobile-menu-admin-section"
            class="pl-4 border-l border-gray-700 ml-2 mb-1 flex flex-col gap-1"
          >
            <router-link
              v-if="isSuperAdmin || (isAdmin && organizationAllowsChildren)"
              :to="'/admin/organizations'"
              class="mobile-nav-link py-3 px-3 rounded-md hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10 text-sm"
              @click="closeMenu"
            >
              {{ $t('Header.Button08') }}
            </router-link>
            <router-link
              v-if="isSuperAdmin || isAdmin"
              :to="'/admin/users'"
              class="mobile-nav-link py-3 px-3 rounded-md hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10 text-sm"
              @click="closeMenu"
            >
              {{ $t('Header.Button09') }}
            </router-link>
            <router-link
              v-if="canAdministerBokvalitet"
              :to="'/framework-manager'"
              class="mobile-nav-link py-3 px-3 rounded-md hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10 text-sm"
              @click="closeMenu"
            >
              {{ $t('Header.Button11') }}
            </router-link>
            <router-link
              v-if="isSuperAdmin"
              :to="'/cms'"
              class="mobile-nav-link py-3 px-3 rounded-md hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10 text-sm"
              @click="closeMenu"
            >
              {{ $t('Header.Button07') }}
            </router-link>
            <router-link
              v-if="isSuperAdmin"
              :to="'/frameworks'"
              class="mobile-nav-link py-3 px-3 rounded-md hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10 text-sm"
              @click="closeMenu"
            >
              {{ $t('Header.Button13') }}
            </router-link>
            <router-link
              v-if="isSuperAdmin || isAdmin"
              :to="'/admin/license-requests'"
              class="mobile-nav-link py-3 px-3 rounded-md hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10 text-sm"
              @click="closeMenu"
            >
              {{ $t('Bokvalitetsrosen.license_requests.License Requests') }}
            </router-link>
          </div>

          <router-link
            v-if="!isLink"
            to="/support"
            class="mobile-nav-link py-3 px-3 rounded-md text-white hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10"
            @click="closeMenu"
          >
            {{ $t('Header.Button10') }}
          </router-link>

          <router-link
            v-if="!isLink"
            :to="{ path: '/', hash: '#abonnement-og-priser' }"
            class="mobile-nav-link py-3 px-3 rounded-md text-white hover:bg-gray-500 hover:bg-opacity-10 router-link-active:bg-gray-500 router-link-active:bg-opacity-10"
            @click="closeMenu"
          >
            Abonnement
          </router-link>
        </nav>

        <div class="my-5 border-t border-gray-700"></div>

        <div class="flex flex-col gap-4">
          <div v-if="!user" class="rounded-md border border-gray-700">
            <button
              class="w-full py-3 px-3 flex items-center justify-between hover:bg-gray-500 hover:bg-opacity-10"
              type="button"
              data-cy="headerLoginButton"
              @click="authAction"
            >
              <span class="flex items-center">
                <BaseIcon
                  icon="BaseUser"
                  class="mr-2"
                  color="white"
                />
                {{ $t('Header.Button04') }}
              </span>
            </button>
          </div>

          <div v-else class="rounded-md border border-gray-700">
            <button
              class="w-full py-3 px-3 flex items-center justify-between hover:bg-gray-500 hover:bg-opacity-10"
              type="button"
              :aria-expanded="showAccountMenu.toString()"
              aria-controls="mobile-menu-account-section"
              @click="showAccountMenu = !showAccountMenu"
            >
              <span class="flex items-center">
                <BaseIcon
                  icon="BaseUser"
                  class="mr-2"
                  color="white"
                />
                {{ user.name }}
              </span>
              <BaseIcon
                icon="BaseChevronDown"
                size="sm"
                color="white"
                :class="showAccountMenu ? 'rotate-180' : ''"
              />
            </button>

            <div v-if="showAccountMenu" id="mobile-menu-account-section" class="border-t border-gray-700 py-1">
              <button
                v-for="option in userMenuOptions"
                :key="option.value"
                class="w-full text-left px-3 py-3 hover:bg-gray-500 hover:bg-opacity-10"
                type="button"
                @click="handleUserAction(option)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="rounded-md border border-gray-700">
            <button
              class="w-full py-3 px-3 flex items-center justify-between hover:bg-gray-500 hover:bg-opacity-10"
              type="button"
              :aria-expanded="showLanguageMenu.toString()"
              aria-controls="mobile-menu-language-section"
              @click="showLanguageMenu = !showLanguageMenu"
            >
              <span class="flex items-center">
                <BaseIcon icon="BaseGlobe" class="mr-2" color="white" />
                {{ currentLanguageLabel }}
              </span>
              <BaseIcon
                icon="BaseChevronDown"
                size="sm"
                color="white"
                :class="showLanguageMenu ? 'rotate-180' : ''"
              />
            </button>

            <div
              v-if="showLanguageMenu"
              id="mobile-menu-language-section"
              class="border-t border-gray-700 py-1"
              role="listbox"
              :aria-label="$t('Framework Manager.Questions.language_select')"
            >
              <button
                v-for="option in languages"
                :key="option.value"
                class="w-full text-left px-3 py-3 hover:bg-gray-500 hover:bg-opacity-10 flex items-center justify-between"
                type="button"
                role="option"
                :aria-selected="(option.value === language).toString()"
                :class="option.value === language ? 'bg-gray-500 bg-opacity-20' : ''"
                @click="selectLanguage(option.value)"
              >
                <span>{{ option.label }}</span>
                <BaseIcon
                  v-if="option.value === language"
                  icon="BaseCheck"
                  size="sm"
                  color="white"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/shared/BaseButton.vue'
import BaseIcon from '@/components/shared/BaseIcon.vue'
import { isLinkKompassOrigin } from '@/composables/domainUtils'
import languages from '@/i18n/languages'

export default {
  components: {
    BaseButton,
    BaseIcon,
  },
  props: {
    darkBg: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      menuCollapsed: true,
      showAdminMenu: false,
      showAccountMenu: false,
      showLanguageMenu: false,
      previousBodyOverflow: '',
      language: this.$store.state.cmsLocale,
      languages: languages,
    }
  },
  computed: {
    user () {
      return this.$store.getters['auth/user']
    },
    userMenuOptions () {
      return [
        { value: 'log out', label: this.$t('Header.Button04_alt') },
        { value: 'change password', label: this.$t('Header.Button05') },
      ]
    },
    currentLanguageLabel () {
      const activeLanguage = this.languages.find(option => option.value === this.language)
      return activeLanguage ? activeLanguage.label : ''
    },
    isLink () {
      return isLinkKompassOrigin()
    },
    isSuperAdmin () {
      return this.$store.getters['auth/isAuthenticated'] && this.$store.getters['auth/user'].roleId === 3
    },
    isAdmin () {
      return this.$store.getters['auth/isAuthenticated'] && this.$store.getters['auth/user'].roleId === 4
    },
    hasAccessToBokvalitet () {
      if (!this.user || !Array.isArray(this.user.frameworkIds)) return false
      const frameworks = this.$store.getters['frameworks/list'] || []
      return frameworks
        .filter(framework => this.user.frameworkIds.includes(framework.id))
        .some(framework => framework.id === 4)
    },
    canAdministerBokvalitet () {
      return this.isSuperAdmin || (this.isAdmin && this.hasAccessToBokvalitet && this.organizationAllowsChildren)
    },
    organizationAllowsChildren () {
      if (!this.user) return false
      if (this.user.roleId === 2) return false
      const organization = this.$store.getters['organizations/get'](this.user.organizationId)
      if (!organization) return false
      return organization.allowChildren
    },
  },
  watch: {
    $route () {
      if (!this.menuCollapsed) this.closeMenu()
    },
    language: {
      immediate: false,
      deep: true,
      async handler (newValue) {
        this.$i18n.locale = newValue
        this.$store.dispatch('setCmsLocale', newValue)
      },
    },
  },
  methods: {
    expandMenu () {
      this.menuCollapsed = false
      this.lockBodyScroll()
      this.$nextTick(() => {
        this.$refs.menuPanel?.focus()
      })
    },
    closeMenu () {
      this.menuCollapsed = true
      this.showAdminMenu = false
      this.showAccountMenu = false
      this.showLanguageMenu = false
      this.unlockBodyScroll()
    },
    async authAction () {
      if (this.user === null) {
        this.$router.push('/login')
        this.closeMenu()
      } else {
        await this.$store.dispatch('auth/logout')
        location.href = '/'
      }
    },
    handleUserAction (option) {
      if (option.value === 'log out') {
        this.authAction()
      } else if (option.value === 'change password') {
        this.closeMenu()
        this.$router.push('/change-password')
      }
    },
    selectLanguage (value) {
      this.language = value
      this.showLanguageMenu = false
    },
    lockBodyScroll () {
      if (typeof document === 'undefined') return
      this.previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    },
    unlockBodyScroll () {
      if (typeof document === 'undefined') return
      document.body.style.overflow = this.previousBodyOverflow || ''
    },
  },
  async mounted () {
    if (this.user && this.user.organizationId) {
      await this.$store.dispatch('organizations/get', this.user.organizationId)
    }
  },
  beforeUnmount () {
    this.unlockBodyScroll()
  },
}
</script>

<style scoped>
</style>
